// **Derived UI/HUD visibility** (`demo-presentation-shell` task 3.9's HUD-restore scenario;
// design DD13's "the HUD in the state that chapter's `ui` cues establish at that cursor, never a
// union of what was previously shown"). Like the sequencer's slide/reveal state, this is a **pure
// function of the cursor and the cue list** — never accumulated, mutated or reset by hand — so a
// jump or a stage reset restoring the correct HUD is true by construction rather than a second
// code path a future change can let drift from the first. Group 8 (cue-gated HUD, both modals)
// renders from this; this module owns only what is *visible*, not how.

import type { ChapterId, Cue, DemoNavTarget, ModalId, UiToken } from './cues';
import { CUE_LIST, isModal } from './cues';

/**
 * **What the projector is showing at a cursor** (task 12.2's composition, design **DD2**).
 *
 * Only three cue kinds change the scene: a `slide` shows the deck, a `stage` shows the live world,
 * a `nav` shows a tournament chapter. `reveal` and `ui` cues deliberately do **not** — a reveal is
 * a step *within* the slide already up, and the whole point of the cue-gated HUD is that it
 * arrives over a scene that stays put. So the scene is the last scene-setting cue at or before the
 * cursor, derived like everything else from the one integer rather than tracked beside it.
 *
 * The slide's own id and reveal step are **not** restated here: `sequencer.ts` already derives
 * both, and a second derivation is exactly the drift DD2 rules out.
 */
export type DemoScene =
  | { readonly kind: 'slide' }
  | { readonly kind: 'stage' }
  | { readonly kind: 'nav'; readonly target: DemoNavTarget }
  /** Before the first scene-setting cue — reachable only by a substitute cue list that opens on a
   * `ui` or `reveal` cue, never by the shipped one. */
  | { readonly kind: 'none' };

export function deriveScene(cursor: number, cueList: readonly Cue[] = CUE_LIST): DemoScene {
  let scene: DemoScene = { kind: 'none' };
  for (let i = 0; i <= cursor && i < cueList.length; i++) {
    const cue = cueList[i];
    if (cue === undefined) continue;
    if (cue.kind === 'slide') scene = { kind: 'slide' };
    else if (cue.kind === 'stage') scene = { kind: 'stage' };
    else if (cue.kind === 'nav') scene = { kind: 'nav', target: cue.target };
  }
  return scene;
}

/** The three chapter-title chrome tokens. */
export type ChapterTitleToken = 'chapter1Title' | 'chapter2Title' | 'chapter3Title';

const CHAPTER_TITLES: ReadonlySet<UiToken> = new Set<UiToken>([
  'chapter1Title',
  'chapter2Title',
  'chapter3Title',
]);

function isChapterTitle(token: UiToken): token is ChapterTitleToken {
  return CHAPTER_TITLES.has(token);
}

export interface DemoUiState {
  /** Every non-modal token a `ui` cue has shown, at or before the cursor. Once shown, a HUD
   * element (or chrome token) stays shown for the rest of its chapter — only the two modals ever
   * have a "hide" cue (`show: null`). */
  readonly visible: ReadonlySet<UiToken>;
  /** The currently open modal, or `null` if none — the last modal-affecting `ui` cue at or before
   * the cursor. */
  readonly activeModal: ModalId | null;
  /**
   * **The chapter title the cursor is actually under** — the *most recent* title token at or
   * before the cursor, cleared the moment the cursor leaves that chapter.
   *
   * It cannot be read off {@link DemoUiState.visible}, and that is the point: `visible`
   * accumulates, so by chapter 3 it holds all three title tokens at once and any caller picking
   * "the first one present" pins chapter 1's title to the projector for the rest of the show,
   * painted over every later scene. Most-recent-wins is derived here, once, rather than left to
   * each renderer to get right.
   */
  readonly activeTitle: ChapterTitleToken | null;
  /**
   * **The numbered chapter the cursor stands in**, or `undefined` outside all three.
   *
   * Derived here because it is the same backward scan {@link DemoUiState.activeTitle} already
   * makes, and because the renderer needs it for a different job: any chrome holding *component*
   * state (a prompt bar's failure, a type editor's open draft) has to be told when the show has
   * moved on. `visible` cannot say that — it accumulates deliberately, so the HUD survives a jump
   * — which is how chapter 1's "Couldn't reach the hive." ended up burned into every chapter-2 and
   * chapter-3 screenshot of the E2E drive (finding N7).
   */
  readonly chapter: ChapterId | undefined;
}

export function deriveUiState(cursor: number, cueList: readonly Cue[] = CUE_LIST): DemoUiState {
  const visible = new Set<UiToken>();
  let activeModal: ModalId | null = null;
  let activeTitle: ChapterTitleToken | null = null;
  let chapter: ChapterId | undefined;
  for (let i = 0; i <= cursor && i < cueList.length; i++) {
    const cue = cueList[i];
    if (cue === undefined) {
      continue;
    }
    // Every cue carries the chapter it belongs to (or none), so a chapter boundary — including
    // the one out of chapter 3 into the wrap-up, which no `ui` cue marks — is visible here.
    if (cue.chapter !== chapter) {
      chapter = cue.chapter;
      activeTitle = null;
    }
    // **A `configure` establishes the nectar counter** (presentation-polish Wave 2, 2026-08-11).
    //
    // The counter used to take its own `ui-nectar-counter` cue, sitting between the configure and
    // the chapter title, so the presenter's *first* click after the fade-in bought nothing but a
    // number appearing and the title — the thing that announces the chapter — needed a second. The
    // counter is part of what "fade to the game screen" means (the beat manifest's own configure
    // description says so), not a beat of its own.
    //
    // It is folded in **here** rather than left to the renderer, and that closes an older
    // inconsistency: `DemoApp` used to render this one element on `showNectar={onStage}` while the
    // other three HUD elements read `visible`. Two gating mechanisms for one HUD, and `visible` —
    // which `sequencer.test.ts` reads as the HUD's state after a reset — did not mention the counter
    // at all. Now every HUD token is established by a cue and read the same way.
    //
    // Not by giving the configure cue a `show` field: `ui` cues are the kind whose whole job is
    // chrome, and a `stage` cue carrying chrome tokens would give two kinds the same
    // responsibility. This stays a pure function of the cursor and the cue list, which is the
    // module's own invariant.
    if (cue.kind === 'stage' && cue.op.kind === 'configure') {
      visible.add('nectarCounter');
      continue;
    }
    // The second bee and its counter are one audience beat. The spawn remains the authoritative
    // world operation; deriving the chrome here keeps reverse/reset/chapter-jump reconstruction a
    // pure function of the same cursor.
    if (cue.kind === 'stage' && cue.beatId === 'stage-chapter1-spawn-second-bee') {
      visible.add('beeCounter');
      continue;
    }
    if (cue.kind !== 'ui') {
      continue;
    }
    if (cue.show === null) {
      if (activeModal !== null) {
        visible.delete(activeModal);
      }
      activeModal = null;
      continue;
    }
    if (isModal(cue.show)) {
      activeModal = cue.show;
    }
    if (isChapterTitle(cue.show)) {
      activeTitle = cue.show;
    }
    visible.add(cue.show);
  }
  return { visible, activeModal, activeTitle, chapter };
}
